<script setup lang="ts">
import { computed } from 'vue'
import { capitalize, isEmpty } from 'lodash'
import { VTable } from '@/modules/shared/components'
import VSwitch from '@/modules/shared/components/VSwitch.vue'
import { InProgress, Column } from '../types'
import { useEntityStore } from '../stores/entity-store'
import TaskStatusCheckBox from '@/modules/tasking/components/TaskStatusCheckBox.vue'

const props = defineProps<{
  entities: InProgress[]
  columns: Column[]
  skeleton: boolean
  showOnHoldToggle?: boolean
  emptyMessage?: string
}>()

const entityStore = useEntityStore()
const isEmptyState = computed(() => !props.skeleton && (!props.entities || props.entities.length === 0))
</script>

<template>
  <div
    v-if="isEmptyState"
    class="rounded-md border border-dashed border-gray-300 bg-gray-50 py-12 text-center text-sm text-gray-500"
  >
    {{ emptyMessage ?? 'Nothing to display.' }}
  </div>
  <VTable
    v-else
    ref="table"
    :columns="[
      {
        key: 'name',
        name: capitalize('Investor Name'),
        type: 'string',
        is_visible: true,
        fixed: true,
      },
      ...columns,
    ]"
    :items="entities"
    name="in-progress-entities"
    :skeleton="skeleton"
  >
    <template #name="{ item }">
      <div class="flex items-start justify-between gap-4">
        <!-- Left Side -->
        <div class="min-w-0">
          <RouterLink
            class="hyperlink font-medium text-blue-700 hover:underline"
            :to="{
              name: 'investing.entity-overview',
              params: {
                entity_id: item.id,
                entity_type: item.entity_type,
                slug: item.group_subdomain,
              },
            }"
          >
            {{ item.name }}
          </RouterLink>

          <div v-if="!isEmpty(item.legal_name)" class="mt-1 text-sm text-gray-600">
            {{ item.legal_name }}
          </div>
        </div>

        <!-- Right Side Toggle -->
        <div v-if="showOnHoldToggle" class="flex shrink-0 items-center gap-2 border-l border-gray-300 pl-4">
          <span class="whitespace-nowrap text-xs text-gray-500"> On Hold </span>

          <VSwitch :enabled="item.on_hold" @click.prevent="entityStore.toggleOnHold(item)" />
        </div>
      </div>
    </template>
    <template v-for="column in columns" #[column.key]="{ item }" :key="column.key">
      <div v-if="column.key.startsWith('completed_')">
        <TaskStatusCheckBox v-if="item[column.key] != null" :checked="!!item[column.key]" :disabled="true" />
      </div>
    </template>
  </VTable>
</template>
